import React, { useState, useRef, useEffect } from "react";
import { Reply as ReplyIcon, MoreVertical, Trash2, Pin, PinOff } from "lucide-react";
import Avatar from "components/ui/Avatar";
import RoleBadge from "modules/swiftboard/components/ui/RoleBadge";
import { getRelativeTime } from "services/date-converter";
import { getDisplayIdentity } from "utils/user-identity";
import CommentComposer from "./CommentComposer";

// Overflow menu (kebab) for a single comment — houses whichever of
// Pin/Unpin (ADMIN/SUPER_ADMIN, top-level only) and Delete (own comment, or
// any comment for a moderator) actually apply; renders nothing if neither does.
const CommentMenu = ({ isPinned, onPin, onDelete }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handleOutside = (e) => {
      if (!ref.current?.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handleOutside);
    return () => document.removeEventListener("mousedown", handleOutside);
  }, [open]);

  if (!onPin && !onDelete) return null;

  return (
    <div ref={ref} className="absolute top-0 right-0">
      <button type="button" onClick={() => setOpen((v) => !v)} className="p-1 rounded-md text-text_muted" title="More">
        <MoreVertical size={15} />
      </button>
      {open && (
        <div className="absolute right-0 mt-1 w-40 rounded-lg border border-border bg-surface shadow-lg py-1 z-10">
          {onPin && (
            <button
              type="button"
              onClick={() => {
                onPin();
                setOpen(false);
              }}
              className="w-full flex items-center gap-2 px-3 py-1.5 text-[12px] font-medium text-text_secondary"
            >
              {isPinned ? <PinOff size={12} /> : <Pin size={12} />}
              {isPinned ? "Unpin comment" : "Pin comment"}
            </button>
          )}
          {onDelete && (
            <button
              type="button"
              onClick={() => {
                onDelete();
                setOpen(false);
              }}
              className="w-full flex items-center gap-2 px-3 py-1.5 text-[12px] font-medium text-danger"
            >
              <Trash2 size={12} />
              Delete
            </button>
          )}
        </div>
      )}
    </div>
  );
};

const CommentRow = ({ comment, currentUser, isReply, canModerate, onReply, onDelete, onPin }) => {
  const [replying, setReplying] = useState(false);

  // Ownership is checked on the real user id, never on the displayed
  // identity — a SUPER_ADMIN commenting as "Surya Group" can still manage
  // their own comment.
  const isOwn = comment.user?.id === currentUser?.id;
  const canDelete = (isOwn || canModerate) && !!onDelete;
  const canPin = !isReply && canModerate && !!onPin;
  const identity = getDisplayIdentity(comment.user);

  return (
    <div
      className={`relative ${
        isReply
          ? "rounded-xl bg-surface_alt px-3.5 py-3"
          : comment.is_pinned
          ? "rounded-xl bg-warning_bg/40 px-3.5 py-3"
          : ""
      }`}
    >
      <div className="flex items-start gap-2.5">
        <div className="flex-shrink-0 mt-0.5">
          <Avatar
            user={identity?.avatarUser}
            size={isReply ? 28 : 34}
            fit={identity?.isBrand ? "contain" : "cover"}
          />
        </div>
        <div className="flex-1 min-w-0 pr-6">
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="text-[13px] font-bold text-text_primary">{identity?.name}</span>
            <RoleBadge role={identity?.role} />
            {comment.is_pinned && (
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wide bg-warning_bg text-warning">
                <Pin size={9} className="fill-current" />
                Pinned
              </span>
            )}
            <span className="text-text_muted text-[11px]">&bull;</span>
            <span className="text-[11.5px] text-text_muted">{getRelativeTime(comment.created_date)}</span>
          </div>

          <p className="text-[13.5px] text-text_secondary leading-relaxed mt-1 whitespace-pre-wrap break-words">
            {comment.comment}
          </p>

          {!isReply && onReply && (
            <button
              type="button"
              onClick={() => setReplying((v) => !v)}
              className="inline-flex items-center gap-1 mt-2 text-[11.5px] font-semibold text-text_muted"
            >
              <ReplyIcon size={12} />
              Reply
            </button>
          )}

          {replying && (
            <div className="mt-2">
              <CommentComposer
                currentUser={currentUser}
                placeholder={`Reply to ${identity?.name || "this comment"}...`}
                submitLabel="Reply"
                onSubmit={(text) => onReply(comment.id, text)}
                onCancel={() => setReplying(false)}
              />
            </div>
          )}
        </div>
      </div>

      <CommentMenu
        isPinned={comment.is_pinned}
        onPin={canPin ? () => onPin(comment.id) : null}
        onDelete={canDelete ? () => onDelete(comment.id) : null}
      />
    </div>
  );
};

// One top-level comment + its (single-level) replies, per tbl_kb_comment's
// self-relation. Top-level comments stay plain (no card) for an airy feed
// look — pinned ones get a soft highlighted card instead; replies get a
// shaded card + left indent guide to read as nested.
const CommentThread = ({ comment, currentUser, canModerate, onReply, onDelete, onPin }) => (
  <div>
    <CommentRow
      comment={comment}
      currentUser={currentUser}
      canModerate={canModerate}
      onReply={onReply}
      onDelete={onDelete}
      onPin={onPin}
    />
    {comment.replies?.length > 0 && (
      <div className="mt-3 ml-11 pl-3.5 border-l-2 border-border space-y-3">
        {comment.replies.map((reply) => (
          <CommentRow
            key={reply.id}
            comment={reply}
            currentUser={currentUser}
            canModerate={canModerate}
            isReply
            onDelete={onDelete}
          />
        ))}
      </div>
    )}
  </div>
);

export default CommentThread;
